import React from 'react';
import { Button, Spin, Typography } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, WalletOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { LinkHandoffState } from '../hooks/useMultiWallet';
import { NETWORKS, truncateAddress } from '../constants/networks';

const { Text, Title } = Typography;

/**
 * Экран привязки внутри встроенного браузера кошелька.
 *
 * Здесь человек оказывается, придя по ссылке «Открыть в приложении кошелька»:
 * страница загрузилась внутри Trust, Bitget, OKX, TronLink или Phantom, и
 * дальше всё происходит без его участия — ждём провайдер, спрашиваем адрес,
 * привязываем к аккаунту. Экран нужен, чтобы эти несколько секунд не выглядели
 * как зависшая страница, а результат не приходилось угадывать.
 *
 * Про возврат честно. Автоматически вернуть человека из встроенного браузера
 * кошелька в установленное приложение нельзя: на iOS PWA вообще не открывается
 * по ссылке из чужого WebView, на Android это зависит от того, перехватит ли
 * ссылку установленное приложение. Поэтому кнопка возврата — попытка, а текст
 * рядом говорит главное: привязка уже на сервере, и баланс будет виден в
 * приложении, даже если переключаться придётся руками.
 */

interface WalletLinkHandoffProps {
  state: LinkHandoffState;
}

function describeError(code: string | null, t: (k: string) => string): string {
  switch (code) {
    case 'link_blocked':
      return t('The server refused the link request from this wallet browser. Update the app and try again.');
    case 'wallet_not_injected':
      return t('This page is not open inside a wallet app, so there is no wallet to connect. Open it from the wallet list in KredaFi.');
    case 'user_rejected':
      return t('You declined the connection in the wallet.');
    case 'link_expired':
      return t('The connect link has expired. Start connecting again in KredaFi.');
    case 'link_already_linked':
      return t('This wallet is already linked to another account.');
    case 'link_unauthorized':
      return t('The connect link is no longer valid. Start connecting again in KredaFi.');
    case 'link_invalid_address':
      return t('The wallet returned an address this network does not accept.');
    default:
      return t('Could not link the wallet. Try again.');
  }
}

/** Попытка вернуться в приложение. Работает не везде — см. комментарий выше. */
function returnToApp(): void {
  if (typeof window === 'undefined') return;
  const target = `${window.location.origin}${window.location.pathname}`;
  const isAndroid = /Android/i.test(navigator.userAgent);
  if (isAndroid) {
    try {
      const { host, pathname } = window.location;
      // Android отдаёт ссылку приложению, которое её обслуживает: установленному
      // PWA, если оно подтверждено assetlinks.json, иначе браузеру по умолчанию.
      window.location.href = `intent://${host}${pathname}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
      return;
    } catch {
      /* WebView не понял intent-ссылку — уходим в обычный переход */
    }
  }
  window.location.href = target;
}

export const WalletLinkHandoff: React.FC<WalletLinkHandoffProps> = ({ state }) => {
  const { t } = useTranslation();
  if (!state.active) return null;

  const networkName = state.chain ? NETWORKS[state.chain].name : '';
  const busy = state.status === 'connecting' || state.status === 'linking';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000000,
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 360, width: '100%' }}>
        {busy && (
          <>
            <Spin size="large" />
            <Title level={4} style={{ marginTop: 24, marginBottom: 8 }}>
              {state.status === 'connecting'
                ? t('Connecting the wallet')
                : t('Linking the wallet to your account')}
            </Title>
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5, display: 'block' }}>
              {state.status === 'connecting'
                ? t('Confirm the connection in the wallet. If nothing appeared, wait a couple of seconds — the wallet is still loading.')
                : `${state.walletLabel || ''} · ${truncateAddress(state.address || '')}`}
            </Text>
          </>
        )}

        {state.status === 'done' && (
          <>
            <CheckCircleFilled style={{ fontSize: 52, color: '#52c41a' }} />
            <Title level={4} style={{ marginTop: 20, marginBottom: 8 }}>
              {t('Wallet linked')}
            </Title>
            <Text style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
              <WalletOutlined /> {state.walletLabel} · {networkName}
            </Text>
            <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
              {state.address}
            </Text>
            <Button type="primary" size="large" block style={{ marginTop: 24, height: 48, borderRadius: 12 }} onClick={returnToApp}>
              {t('Back to KredaFi')}
            </Button>
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5, display: 'block', marginTop: 12 }}>
              {t('The address is already saved to your account. If the app does not open by itself, switch to it manually — the balance will be there.')}
            </Text>
          </>
        )}

        {state.status === 'error' && (
          <>
            <CloseCircleFilled style={{ fontSize: 52, color: '#ff4d4f' }} />
            <Title level={4} style={{ marginTop: 20, marginBottom: 8 }}>
              {t('Wallet not linked')}
            </Title>
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5, display: 'block' }}>
              {describeError(state.errorCode, t as (k: string) => string)}
            </Text>
            {/*
              Техническая строка нужна не человеку, а отчёту. Один и тот же
              экран «не удалось привязать» означал и просроченный токен, и 403
              от middleware, и обрыв сети — три разные починки. Пока сюда не
              выводился код, каждая жалоба стоила сессии разбирательств.
            */}
            {state.errorDetail && (
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  display: 'block',
                  marginTop: 10,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: '#bfbfbf',
                  wordBreak: 'break-word',
                }}
              >
                {state.errorDetail}
              </Text>
            )}
            <Button size="large" block style={{ marginTop: 24, height: 48, borderRadius: 12 }} onClick={state.retry}>
              {t('Try again')}
            </Button>
            <Button type="text" block style={{ marginTop: 8 }} onClick={state.dismiss}>
              {t('Close')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default WalletLinkHandoff;
